import { useState, type FormEvent } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';

const navItems = [
  { label: '首页', to: '/' },
  { label: '影片分类', to: '/category' },
  { label: '影视资讯', to: '/news' },
];

export default function Navbar() {
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState('');
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    if (!value) return;
    navigate(`/search?q=${encodeURIComponent(value)}`);
    setMobileOpen(false);
  };

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    `relative px-1 py-2 text-sm font-medium transition-colors ${
      isActive
        ? 'text-primary-700'
        : 'text-foreground-600 hover:text-primary-700'
    }`;

  return (
    <header className="sticky top-0 z-50 w-full border-b border-background-200 bg-background-50/95 backdrop-blur">
      <div className="w-full px-4 md:px-8 h-16 md:h-[72px] flex items-center justify-between gap-4">
        <div className="flex items-center gap-8 min-w-0">
          <Link to="/" className="flex items-center gap-2.5 shrink-0" aria-label="青苹果影院首页">
            <span className="w-10 h-10 rounded-lg bg-primary-500 text-background-50 flex items-center justify-center">
              <i className="ri-movie-2-line text-xl" aria-hidden="true" />
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading font-semibold text-lg tracking-tight text-foreground-950">
                青苹果影院
              </span>
              <span className="mt-1 text-[10px] tracking-[0.18em] text-foreground-500 font-label uppercase">
                Green Apple Cinema
              </span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-7" aria-label="主导航">
            {navItems.map((item) => (
              <NavLink key={item.to} to={item.to} end={item.to === '/'} className={navLinkClass}>
                {({ isActive }) => (
                  <span className="flex flex-col items-center">
                    {item.label}
                    <span
                      className={`mt-1 h-0.5 w-4 rounded-full transition-colors ${
                        isActive ? 'bg-accent-500' : 'bg-transparent'
                      }`}
                    />
                  </span>
                )}
              </NavLink>
            ))}
          </nav>
        </div>

        <div className="flex items-center gap-2 md:gap-3">
          <form
            onSubmit={handleSearch}
            className="hidden md:flex items-center gap-2 h-10 pl-4 pr-1.5 rounded-full bg-background-100 border border-background-200 focus-within:border-primary-300 transition-colors w-52 lg:w-72"
            role="search"
          >
            <i className="ri-search-line text-base text-foreground-500" aria-hidden="true" />
            <input
              type="search"
              name="q"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索影片、剧集"
              aria-label="搜索影片"
              className="flex-1 min-w-0 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 outline-none"
            />
            <button
              type="submit"
              className="shrink-0 h-7 px-3 rounded-full bg-primary-500 text-background-50 text-xs font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
            >
              搜索
            </button>
          </form>

          <Link
            to="/category?tag=hot"
            className="hidden lg:flex items-center gap-1.5 h-10 px-4 rounded-full border border-accent-200 bg-accent-50 text-accent-700 text-sm font-medium whitespace-nowrap hover:bg-accent-100 transition-colors"
          >
            <i className="ri-fire-line text-base" aria-hidden="true" />
            热门榜单
          </Link>

          <button
            type="button"
            onClick={() => setMobileOpen((open) => !open)}
            aria-expanded={mobileOpen}
            aria-label={mobileOpen ? '关闭菜单' : '打开菜单'}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg border border-background-200 text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
          >
            <i className={mobileOpen ? 'ri-close-line text-xl' : 'ri-menu-3-line text-xl'} aria-hidden="true" />
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t border-background-200 bg-background-50 animate-fade-in">
          <div className="px-4 py-4 flex flex-col gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  `flex items-center justify-between px-3 py-3 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-primary-50 text-primary-700'
                      : 'text-foreground-700 hover:bg-background-100'
                  }`
                }
              >
                <span>{item.label}</span>
                <i className="ri-arrow-right-s-line text-base text-foreground-400" aria-hidden="true" />
              </NavLink>
            ))}
            <Link
              to="/category?tag=hot"
              onClick={() => setMobileOpen(false)}
              className="flex items-center justify-between px-3 py-3 rounded-lg text-sm font-medium text-accent-700 bg-accent-50"
            >
              <span className="flex items-center gap-2">
                <i className="ri-fire-line" aria-hidden="true" />
                热门榜单
              </span>
              <i className="ri-arrow-right-s-line text-base" aria-hidden="true" />
            </Link>

            <form onSubmit={handleSearch} className="mt-2 flex items-center gap-2 h-11 px-3 rounded-lg bg-background-100 border border-background-200" role="search">
              <i className="ri-search-line text-base text-foreground-500" aria-hidden="true" />
              <input
                type="search"
                name="q"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索影片、剧集"
                aria-label="搜索影片"
                className="flex-1 min-w-0 bg-transparent text-sm outline-none placeholder:text-foreground-400"
              />
              <button type="submit" className="text-sm font-medium text-primary-700 whitespace-nowrap cursor-pointer">
                搜索
              </button>
            </form>
          </div>
        </div>
      )}
    </header>
  );
}